import React, { useState, useEffect } from "react";
import "./DashboardPage.css";
import {
  FaUserGraduate,
  FaBuilding,
  FaUserTie,
  FaClock,
  FaIdBadge,
} from "react-icons/fa";
import { MdCampaign } from "react-icons/md";
import { BsExclamationCircle } from "react-icons/bs";
import { getToken, getEmail } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";
import maleImg from "../assets/profile.png";
import femaleImg from "../assets/girl.png";


/* ---------------- HELPER ---------------- */
const toTitleCase = (text = "") =>
  text
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const Dashboard = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState("");

  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [todayJoining, setTodayJoining] = useState([]);

  const [leaveSummary, setLeaveSummary] = useState({
    CL: 0,
    SL: 0,
    EL: 0,
    CF: 0,
  });

  const [yearsOfService, setYearsOfService] = useState(0);
  const [monthsOfService, setMonthsOfService] = useState(0);
  const [lastLoginTime, setLastLoginTime] = useState("N/A");

  const [announcements, setAnnouncements] = useState([]);
  const [announcementIndex, setAnnouncementIndex] = useState(0);

  const [upcomingHoliday, setUpcomingHoliday] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [rewardIndex, setRewardIndex] = useState(0);

  const token = getToken();
  const email = getEmail();

  /* ---------------- SERVICE CALC ---------------- */
  const calculateService = (joiningDate) => {
    if (!joiningDate) return;

    const start = new Date(joiningDate);
    const today = new Date();

    let years = today.getFullYear() - start.getFullYear();
    let months = today.getMonth() - start.getMonth();

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    setYearsOfService(years);
    setMonthsOfService(months);
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return;
    const date = new Date(lastLogin);
    setLastLoginTime(
      date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  /* ---------------- FILTER BIRTHDAY / ANNIV ---------------- */
  const filterToday = (list = []) => {
    const today = new Date();

    setTodayBirthdays(
      list.filter(
        (e) =>
          e.dob &&
          new Date(e.dob).getDate() === today.getDate() &&
          new Date(e.dob).getMonth() === today.getMonth()
      )
    );

    setTodayJoining(
      list.filter(
        (e) =>
          e.joining_date &&
          new Date(e.joining_date).getDate() === today.getDate() &&
          new Date(e.joining_date).getMonth() === today.getMonth()
      )
    );
  };

  /* ---------------- USER INFO ---------------- */
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await apiClient(`/auth/user-info/?email=${email}`);
        if (!userData) throw new Error("Failed to fetch user data");

        setUserInfo(userData);
        calculateService(userData.joining_date);
        formatLastLogin(userData.last_login);

        const empData = await apiClient("/auth/allemployees/");
        filterToday(Array.isArray(empData) ? empData : []);
      } catch (err) {
        setError(err.message);
      }
    };

    if (token && email) fetchUserData();
  }, [email, token]);

  /* ---------------- LEAVE SUMMARY ---------------- */
  useEffect(() => {
    const fetchLeaves = async () => {
      try {
        const data = await apiClient("/leaves/summary/");
        if (!data) throw new Error("Leave summary fetch failed");

        const getRemaining = (value) => {
          if (!value) return 0;
          const [used, total] = value.split("/").map(Number);
          return Math.max(0, total - used);
        };

        const cfData = await apiClient("/cfleaves/self/");

        setLeaveSummary({
          CL: getRemaining(data.total?.casual_leave),
          SL: getRemaining(data.total?.sick_leave),
          EL: getRemaining(data.total?.earned_leave),
          CF: cfData?.summary?.remaining ?? 0,
        });
      } catch (err) {
        console.error("Leave summary error:", err);
      }
    };

    if (token) fetchLeaves();
  }, [token]);

  /* ---------------- ANNOUNCEMENTS ---------------- */
  useEffect(() => {
    const fetchAnnouncements = async () => {
      const data = await apiClient("/announcements/");
      setAnnouncements(Array.isArray(data) ? data : data?.results || []);
    };

    if (token) fetchAnnouncements();
  }, [token]);

  useEffect(() => {
    if (!announcements.length) return;
    const timer = setInterval(
      () => setAnnouncementIndex((i) => (i + 1) % announcements.length),
      5000
    );
    return () => clearInterval(timer);
  }, [announcements]);

  /* ---------------- HOLIDAY ---------------- */
  useEffect(() => {
    const fetchHoliday = async () => {
      const data = await apiClient("/holiday/");
      const list = Array.isArray(data) ? data : data?.results || [];
      setUpcomingHoliday(
        list.find((h) => new Date(h.date) > new Date()) || null
      );
    };

    if (token) fetchHoliday();
  }, [token]);

  /* ---------------- REWARDS ---------------- */
  useEffect(() => {
    const fetchRewards = async () => {
      const data = await apiClient("/awards/");
      setRewards(Array.isArray(data) ? data : data?.results || []);
    };

    if (token) fetchRewards();
  }, [token]);

  useEffect(() => {
    if (!rewards.length) return;
    const timer = setInterval(
      () => setRewardIndex((i) => (i + 1) % rewards.length),
      5000
    );
    return () => clearInterval(timer);
  }, [rewards]);

   /* ================= PROFILE IMAGE LOGIC ================= */
  const getProfileImage = () => {
    if (!userInfo?.gender) return maleImg;

    return userInfo.gender.toLowerCase() === "female"
      ? femaleImg
      : maleImg;
  };

  /* ========================= RENDER ========================= */
  return (
    <div className="dashboard-container">
      {error && <p style={{ color: "red" }}>{error}</p>}

      <div className="profile-card1">
        <div className="profile-info">
          <img
            src={getProfileImage()}
            alt="User"
            className="profile-pic"
          />

          <div className="text-info1">
            <h2>{toTitleCase(userInfo?.name)}</h2>
            <p><FaIdBadge /> <b>Employee ID:</b> {userInfo?.employee_id}</p>
            <p><FaUserGraduate /> <b>Designation:</b> {userInfo?.designation}</p>
            <p><FaBuilding /> <b>Department:</b> {userInfo?.department}</p>
            <p><FaUserTie /> <b>Reports To:</b> {userInfo?.report_to}</p>
          </div>
        </div>

        <div className="text-info1">
          <h4 className="service-text">
            <span className="service-number">{yearsOfService}</span> years{" "}
            <span className="service-number">{monthsOfService}</span> months
          </h4>
          <p><FaClock /> <b>Last Login:</b> {lastLoginTime}</p>
        </div>
      </div>

      <div className="dashboard-tiles">
        <div className="tile1">
          <div className="tile-header"><MdCampaign /> Announcement</div>
          <div className="tile-body">
            {announcements.length ? (
              <>
                <p><b>{announcements[announcementIndex]?.title}</b></p>
                <p>{announcements[announcementIndex]?.message}</p>
              </>
            ) : <p>No Announcement</p>}
          </div>
        </div>

        <div className="tile1 leave-tile">
          <div className="tile-header">Leave (Total Remaining)</div>
          <div className="tile-body leave-grid">
            {["CL", "SL", "EL", "CF"].map((t) => (
              <div className="leave-item" key={t}>
                <span className="leave-count green">{leaveSummary[t]}</span>
                <p>{t} Remaining</p>
              </div>
            ))}
          </div>
        </div>

        <div className="tile1">
          <div className="tile-header">🎂 Today's Birthday</div>
          <div className="tile-body">
            {todayBirthdays.length
              ? todayBirthdays.map((u) => <p key={u.id}>{u.name}</p>)
              : <p><BsExclamationCircle /> None</p>}
          </div>
        </div>

        <div className="tile1">
          <div className="tile-header">🎉 Work Anniversary</div>
          <div className="tile-body">
            {todayJoining.length
              ? todayJoining.map((u) => <p key={u.id}>{u.name}</p>)
              : <p><BsExclamationCircle /> None</p>}
          </div>
        </div>

        <div className="tile1">
          <div className="tile-header">📅 Upcoming Holiday</div>
          <div className="tile-body">
            {upcomingHoliday ? (
              <>
                <p>{upcomingHoliday.name}</p>
                <p>{upcomingHoliday.date}</p>
              </>
            ) : <p><BsExclamationCircle /> None</p>}
          </div>
        </div>

        <div className="tile1">
          <div className="tile-header">🏆 Rewards</div>
          <div className="tile-body">
            {rewards.length ? (
              <>
                <p><b>{rewards[rewardIndex]?.employeeName}</b></p>
                <p className="reward-title">🏅 {rewards[rewardIndex]?.title}</p>
              </>
            ) : <p><BsExclamationCircle /> None</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
